import React, { useState } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
import DownloadIcon from '@atlaskit/icon/glyph/download';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Popup from '@atlaskit/popup';
import ToolTip from '@atlaskit/tooltip';
import { ButtonItem, PopupMenuGroup, Section } from '@atlaskit/menu';
import { AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { useFeatureToggle } from '@css-xp/base';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { useExportJSON } from '@css-xp/ppc-custom-hooks';
import { ExportJSONTypes } from '@atlassian/temp-proxies';
import { useQuotePoller } from '../hooks/use-quote-poller';

interface QuoteActionProps {
  id: string;
  transactionAccountId: string;
  quoteNumber: string;
}

enum QUOTE_ACTIONS_NAME {
  DOWNLOAD_PDF = 'Download PDF',
}

export const QuoteActions = ({ id, transactionAccountId, quoteNumber }: QuoteActionProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isDownloadingPdf, handleQuoteV2Download } = useQuotePoller(id, transactionAccountId);
  const [isEnabled] = useFeatureToggle();
  const { loading: loadingQuoteJSON } = useExportJSON({
    id,
    transactionAccountId,
    type: ExportJSONTypes.QUOTE,
    analyticsForDownloadedAction: {
      trackSubject: AnalyticsTrackSubject.QUOTE_JSON,
      attributes: {
        quoteId: id,
      },
    },
  });
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  if (isDesignIteration1Enabled) {
    return (
      <ToolTip content={<>Download PDF</>} position="bottom-start" tag="span">
        <LoadingButton
          isLoading={loadingQuoteJSON || isDownloadingPdf}
          onClick={() => {
            handleQuoteV2Download(quoteNumber);
          }}
          iconBefore={
            <DownloadIcon aria-label="Download PDF" label="Download PDF" size="medium" testId="download-pdf" />
          }
          appearance="subtle"
        />
      </ToolTip>
    );
  }

  const renderQuoteActions = () => {
    return (
      <PopupMenuGroup minWidth={100}>
        <Section>
          <ButtonItem
            testId="manage-download-pdf"
            onClick={(e) => {
              e.stopPropagation();
              handleQuoteV2Download(quoteNumber);
              setIsOpen(false);
            }}
          >
            {QUOTE_ACTIONS_NAME.DOWNLOAD_PDF}
          </ButtonItem>
          {/* <ButtonItem testId="manage-place-order">{QUOTE_ACTIONS_NAME.PLACE_ORDER}</ButtonItem> */}
        </Section>
      </PopupMenuGroup>
    );
  };

  return loadingQuoteJSON || isDownloadingPdf ? (
    <LoadingButton className="quote-action-spinner" testId="quote-action-spinner" isLoading />
  ) : (
    <Popup
      testId="quote-action"
      isOpen={isOpen}
      content={renderQuoteActions}
      placement={'bottom-start'}
      onClose={() => setIsOpen(false)}
      trigger={(triggerProps) => (
        <Button
          {...triggerProps}
          isSelected={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          iconBefore={<MoreIcon label="More" />}
          testId="quote-action-trigger"
        />
      )}
    />
  );
};
